// Spec s5.3: a timer, not a stream.
//
// Every tick: if new finals have arrived, render the ENTIRE transcript and ask
// for the complete current answer. Recompute, never accumulate - that is what
// makes a mid-call correction work with no reconciliation logic. A value stated
// at minute 3 and corrected at minute 12 comes back corrected, because the pass
// at minute 12 sees both.
//
// Failure is never fatal: log it, keep the previous state, let the next pass
// try. A failed pass must never clear the panel.

import type { ChecklistConfig } from '../config/types';
import { validateExtraction, type FieldState } from './extraction';
import { requestExtraction, type ExtractionUsage } from './extractionClient';
import type { TranscriptBuffer } from './TranscriptBuffer';

export const EXTRACTION_INTERVAL_MS = 7000;

export type LoopEvents = {
  onState: (fields: FieldState[]) => void;
  onInFlight: (inFlight: boolean) => void;
  onUsage: (usage: ExtractionUsage & { calls: number }) => void;
  onError: (message: string) => void;
};

type ExtractionSource = typeof requestExtraction;

export class ExtractionLoop {
  private timer: ReturnType<typeof setInterval> | null = null;
  private inFlight = false;
  private lastVersion = -1;
  private totals = { calls: 0, inputTokens: 0, outputTokens: 0, costUsd: 0 };

  constructor(
    private readonly buffer: TranscriptBuffer,
    private readonly getConfig: () => ChecklistConfig | null,
    private readonly events: LoopEvents,
    private readonly intervalMs: number = EXTRACTION_INTERVAL_MS,
    private readonly extract: ExtractionSource = requestExtraction,
  ) {}

  get usage(): ExtractionUsage & { calls: number } {
    return { ...this.totals };
  }

  start(): void {
    if (this.timer) return;
    this.lastVersion = -1;
    this.timer = setInterval(() => void this.tick(), this.intervalMs);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  /** Exposed so the UI can force a pass; obeys the same guards as a tick. */
  async runOnce(): Promise<void> {
    await this.tick();
  }

  private async tick(): Promise<void> {
    // Passes must not overlap. If one is still in flight, skip this tick.
    if (this.inFlight) return;

    const config = this.getConfig();
    if (!config || config.fields.length === 0) return;

    // Nothing new since the last pass: do not burn a call on an unchanged
    // transcript.
    if (this.buffer.version === this.lastVersion) return;

    const version = this.buffer.version;
    const transcript = this.buffer.render();
    if (!transcript.trim()) return;

    this.inFlight = true;
    this.events.onInFlight(true);

    try {
      const response = await this.extract(config, transcript);
      // Replace panel state wholesale. Never merge into a previous result.
      this.events.onState(validateExtraction(config.fields, response.fields, transcript));

      this.totals = {
        calls: this.totals.calls + 1,
        inputTokens: this.totals.inputTokens + response.usage.inputTokens,
        outputTokens: this.totals.outputTokens + response.usage.outputTokens,
        costUsd: this.totals.costUsd + response.usage.costUsd,
      };
      this.events.onUsage({ ...this.totals });
      this.lastVersion = version;
    } catch (err) {
      // Keep the previous state. The next tick will try again with a transcript
      // that has only grown.
      this.events.onError(err instanceof Error ? err.message : String(err));
    } finally {
      this.inFlight = false;
      this.events.onInFlight(false);
    }
  }
}
